import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { DialogueBloc } from '@kp/dialogue/dialogue.bloc';
import { PulseSurveyBloc } from '@kp/pulse-survey/pulse-survey.bloc';
import { ProfileDialogueGuard } from '@kp/shared/guards/profile-dialogue.guard';
import { PulseSurveyAvailableGuard } from '@kp/shared/guards/pulse-survey-available.guard';
import { ReturnUrlService } from '@kp/shared/services/return-url.service';
import { Observable, of } from 'rxjs';
import { first, switchMap } from 'rxjs/operators';

export enum Guards {
  PROFILE_DIALOGUE_GUARD = 'PROFILE_DIALOGUE_GUARD',
  PULSE_SURVEY_AVAILABLE_GUARD = 'PULSE_SURVEY_AVAILABLE_GUARD',
}

@Injectable({
  providedIn: 'root',
})
export class TrafficCoordinatorGuard implements CanActivate {
  private route: ActivatedRouteSnapshot;
  private state: RouterStateSnapshot;

  constructor(
    private _dialogueBloc: DialogueBloc,
    private _pulseSurveyBloc: PulseSurveyBloc,
    private _returnUrlService: ReturnUrlService,
    private _router: Router
  ) {}

  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    this.route = route;
    this.state = state;

    if (!this.route.data?.guards?.length) {
      return of(true);
    }
    return this._executeGuards();
  }

  private _executeGuards(guardIndex: number = 0): Observable<boolean | UrlTree> {
    return this._activateGuard(this.route.data.guards[guardIndex]).pipe(
      first(),
      switchMap((guardResult) => {
        if (guardIndex < this.route.data.guards.length - 1 && guardResult === true) {
          return this._executeGuards(guardIndex + 1);
        }
        return of(guardResult);
      })
    );
  }

  private _activateGuard(guardKey: string): Observable<boolean | UrlTree> {
    let guard: ProfileDialogueGuard | PulseSurveyAvailableGuard;

    switch (guardKey) {
      case Guards.PROFILE_DIALOGUE_GUARD:
        guard = new ProfileDialogueGuard(this._dialogueBloc, this._returnUrlService, this._router);
        break;
      case Guards.PULSE_SURVEY_AVAILABLE_GUARD:
        guard = new PulseSurveyAvailableGuard(this._pulseSurveyBloc, this._returnUrlService, this._router);
        break;
      default:
        break;
    }
    return guard.canActivate(this.route, this.state);
  }
}
